import * as React from 'react';
import { useParams } from 'react-router-dom';
import { PageErrorState } from '../../components/shared/PageErrorState';
import { apiFetch } from '../../lib/apiClient';

interface ChatRunEvent {
  id: number;
  runId: string | null;
  socketConnectionId: string;
  chatSessionId: number | null;
  workspaceId: number | null;
  userId: number | null;
  projectId: number | null;
  eventType: string;
  payload: Record<string, unknown> | null;
  ts: string;
}

/**
 * AI Run Trace Page
 *
 * Dev/admin tool for inspecting the ai.chat_run_event rows for a specific run.
 * Accessible at /admin/ai/traces/:runId - workspace admin only.
 *
 * Fetches from GET /api/workspace/admin/ai/traces/:runId.
 */
export function AiRunTracePage() {
  const { runId } = useParams<{ runId: string }>();
  const [events, setEvents] = React.useState<ChatRunEvent[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!runId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ events: ChatRunEvent[] }>(
        `/api/workspace/admin/ai/traces/${encodeURIComponent(runId)}`
      );
      setEvents(data.events ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ padding: '24px', fontFamily: 'monospace' }}>
        Loading trace for run <strong>{runId}</strong>…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', fontFamily: 'monospace' }}>
        <PageErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', fontFamily: 'monospace', fontSize: '13px' }}>
      <h2 style={{ marginBottom: '8px' }}>AI Run Trace</h2>
      <p style={{ color: '#666', marginBottom: '16px' }}>
        Run ID: <code>{runId}</code> - {events.length} events
      </p>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '12px',
        }}
      >
        <thead>
          <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
            <th style={{ padding: '6px 8px', borderBottom: '1px solid #ddd' }}>ts</th>
            <th style={{ padding: '6px 8px', borderBottom: '1px solid #ddd' }}>eventType</th>
            <th style={{ padding: '6px 8px', borderBottom: '1px solid #ddd' }}>payload</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} style={{ borderBottom: '1px solid #eee' }}>
              <td
                style={{
                  padding: '5px 8px',
                  whiteSpace: 'nowrap',
                  color: '#888',
                }}
              >
                {new Date(e.ts).toISOString().replace('T', ' ').slice(0, 23)}
              </td>
              <td
                style={{
                  padding: '5px 8px',
                  fontWeight:
                    e.eventType === 'run_failed' || e.eventType === 'run_completed'
                      ? 'bold'
                      : 'normal',
                  color:
                    e.eventType === 'run_failed'
                      ? 'red'
                      : e.eventType === 'run_completed'
                        ? 'green'
                        : 'inherit',
                }}
              >
                {e.eventType}
              </td>
              <td style={{ padding: '5px 8px', color: '#555' }}>
                {e.payload ? JSON.stringify(e.payload) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
